/**
 * Phase E / Q16 — XP status bar (5 cells).
 *
 * Sits below the XP taskbar, full-width. Uses xp.css's `.status-bar` +
 * `.status-bar-field` semantics so the cells get their authentic
 * inset-bevel look. Data + click handlers unchanged from the Win95
 * build:
 *
 *   - `api.health.summary` — Convex, Portal, Index aggregate
 *   - `api.health.indexHealth` — per-portal index probe
 *   - `subscribeTokenStatus` — client-side MSAL expiry (M365 cell)
 *   - `useUser()` from Clerk — User cell
 *
 * Plan: ~/.claude/plans/it-s-time-to-rustling-locket.md §"Phase E".
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'convex/react';
import { useUser, useClerk } from '@clerk/react';

import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import { useWindowContext } from './WindowContext';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';
import { SETTINGS_SET_TAB_EVENT, type SettingsTab } from '../settings/SettingsWindow';
import {
  getTokenStatus,
  subscribeTokenStatus,
  type TokenStatus,
} from '../../lib/graph/tokenStatus';
import {
  CELL_COLORS,
  type CellModel,
  deriveConvexCell,
  deriveIndexCell,
  deriveM365Cell,
  derivePortalCell,
  deriveUserCell,
} from './statusBarCells';
import { HelpTip } from '../ui/HelpTip';

export const STATUS_BAR_HEIGHT = 24;

interface StatusBarCellProps {
  cell: CellModel;
  testId: string;
  onClick?: () => void;
}

function StatusBarCell({ cell, testId, onClick }: StatusBarCellProps): React.ReactElement {
  const palette = CELL_COLORS[cell.state];
  const interactive = onClick !== undefined;
  return (
    <HelpTip short={cell.tooltip} wrap>
      <button
        type="button"
        data-testid={testId}
        data-cell-state={cell.state}
        onClick={onClick}
        disabled={!interactive}
        className="status-bar-field flex items-center gap-1.5 text-black"
        style={{
          fontFamily: 'var(--xp-font-body)',
          fontSize: 11,
          cursor: interactive ? 'pointer' : 'default',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          background: '#ece9d8',
          border: 'none',
        }}
      >
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: palette.dot,
            flexShrink: 0,
          }}
        />
        <span style={{ color: '#000' }}>{cell.label}</span>
      </button>
    </HelpTip>
  );
}

function useTokenStatusState(): {
  status: TokenStatus;
  expiresOn?: string;
  accountEmail?: string;
} {
  const [snap, setSnap] = useState(() => getTokenStatus());
  useEffect(() => subscribeTokenStatus(setSnap), []);
  return snap;
}

export function StatusBar(): React.ReactElement {
  const { openWindow } = useWindowContext();
  const summary = useQuery(api.health.summary);
  const tokenSnap = useTokenStatusState();

  // D5 — clickable status-bar cells. Each cell that maps to a window
  // opens it; cells that map to a Settings tab fire SETTINGS_SET_TAB
  // after openWindow so the right tab is active.
  function openSettingsAtTab(tab: SettingsTab): void {
    openWindow('/settings');
    setTimeout(() => {
      document.dispatchEvent(
        new CustomEvent(SETTINGS_SET_TAB_EVENT, { detail: { tab } }),
      );
    }, 0);
  }
  const slug = summary?.currentUserPortalSlug ?? null;
  const indexHealth = useQuery(
    api.health.indexHealth,
    slug !== null ? { portalSlug: slug } : 'skip',
  );
  // K.3.b — Resolve the active portal's tenantBranding row so the M365 cell
  // can switch into "Connected to real SharePoint" mode when the portal has
  // a real Graph siteId (e.g. morrison-legal, markmilliner, clearview).
  const branding = useQuery(
    api.tenantBranding.getBySlug,
    slug !== null ? { clientSlug: slug } : 'skip',
  );
  const { isSignedIn, user } = useUser();
  const { signOut } = useClerk();

  // Convex deployment-health signal kept around for Settings →
  // Diagnostics + the existing test surface, but no longer rendered
  // in the status bar (customers don't know what Convex is).
  void deriveConvexCell(summary);
  const tenantSharePoint = (() => {
    // While the branding query is still loading, return undefined so we
    // don't flicker into a demo state. Once the query resolves (null or
    // a row), we decide.
    if (branding === undefined) return undefined;
    if (
      branding === null ||
      !branding.firmSkillsSiteId ||
      branding.firmSkillsSiteId.length === 0
    ) {
      return null;
    }
    return {
      siteId: branding.firmSkillsSiteId,
      driveId: branding.firmSkillsDriveId ?? null,
      displayName: branding.displayName,
      // health.summary doesn't carry a lastSyncAt today — surface the
      // most recent Convex cron tick as a proxy for "this data is fresh".
      lastSyncAt: summary?.lastCronAt
        ? new Date(summary.lastCronAt).getTime()
        : undefined,
    };
  })();
  const m365Cell = deriveM365Cell({
    status: tokenSnap.status,
    expiresOn: tokenSnap.expiresOn,
    accountEmail: tokenSnap.accountEmail,
    tenantSharePoint,
  });
  // 2026-05-28 — Portal cell now reads the URL (the same source
  // every other window uses via useResolvedPortalSlug). Previously
  // it read summary.currentUserPortalSlug from Convex, which drifts
  // from the URL: on `/` the server says "you have portal X" while
  // the URL says "no portal", and the cell would display the portal
  // name even though every window's `useResolvedPortalSlug() === null`
  // path was firing. That contradiction confused returning users.
  // The provisioning-status branch still uses Convex because that
  // info ISN'T in the URL.
  const urlPortalSlug = useResolvedPortalSlug();
  const portalCell = derivePortalCell(
    urlPortalSlug,
    summary?.portalProvisioningStatus ?? null,
  );
  const indexCell = deriveIndexCell(indexHealth ?? null);
  const userCell = deriveUserCell({
    isSignedIn: isSignedIn ?? false,
    displayName: user?.fullName ?? undefined,
    email: user?.primaryEmailAddress?.emailAddress ?? undefined,
  });

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userBtnRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    function onDoc(e: MouseEvent) {
      if (!userBtnRef.current) return;
      if (!userBtnRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [userMenuOpen]);

  // K.3.b — popover on the M365 cell showing the real SharePoint siteId,
  // driveId, and a "View in SharePoint" link. Opens on click; click-away closes.
  const [m365PopoverOpen, setM365PopoverOpen] = useState(false);
  const m365BtnRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!m365PopoverOpen) return;
    function onDoc(e: MouseEvent) {
      if (!m365BtnRef.current) return;
      if (!m365BtnRef.current.contains(e.target as Node)) {
        setM365PopoverOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [m365PopoverOpen]);

  function handleM365Click() {
    // K.3.b — when the active portal has a real SharePoint backing, the
    // click toggles the SharePoint-details popover instead of redirecting
    // to settings. Only fall through to the reconnect flow when there's
    // no real SP and the token is unhealthy.
    if (tenantSharePoint !== null && tenantSharePoint !== undefined) {
      setM365PopoverOpen((v) => !v);
      return;
    }
    if (tokenSnap.status === 'expired' || tokenSnap.status === 'unknown') {
      window.location.hash = '#/settings?focus=microsoft';
    }
  }

  // K.3.b — Build a `*.sharepoint.com` URL to the root of the firm's
  // skills site so evaluators can see the real SharePoint UI in a new tab.
  // The composite siteId from Graph carries the host as its first segment,
  // so we don't need a separate hostname field on tenantBranding.
  const tenantSiteWebUrl = (() => {
    if (!tenantSharePoint) return null;
    const host = tenantSharePoint.siteId.split(',')[0]?.trim() ?? '';
    if (host.length === 0) return null;
    // Slug-based path is the safest unauth-friendly URL; full
    // `/sites/<name>` requires us to know the SP site URL slug, which we
    // don't ship in tenantBranding today.
    return `https://${host}`;
  })();

  return (
    <div
      data-testid={testIds.statusBar.root}
      // xp.css ships .status-bar; we mount it at the viewport bottom (above
      // the taskbar). Inline positioning kept because the bar is fixed
      // relative to the viewport, not the content.
      className="status-bar fixed left-0 right-0 flex items-stretch z-[8500]"
      style={{
        bottom: 'var(--taskbar-height, 58px)',
        height: STATUS_BAR_HEIGHT,
        background: '#ece9d8',
        borderTop: '1px solid rgba(0,0,0,0.2)',
        fontFamily: 'var(--xp-font-body)',
      }}
    >
      {/*
        Convex cell hidden from end-user-facing surface: customers don't
        know or care what Convex is. The deployment-health signal still
        lives at Settings → Diagnostics for operators. `convexCell` /
        `deriveConvexCell` are kept intact so the data + tests don't
        rot, just not rendered in the status bar.
      */}
      <div ref={m365BtnRef} className="relative flex">
        <StatusBarCell
          cell={m365Cell}
          testId={testIds.statusBar.cellM365}
          onClick={
            // Clickable when we have a real tenant SP (toggle popover) OR
            // when the token is unhealthy (reconnect flow). Fallback for
            // anything else: open Settings → Workspace tab where the
            // user can connect their tenant.
            (tenantSharePoint !== null && tenantSharePoint !== undefined) ||
            tokenSnap.status === 'expired' ||
            tokenSnap.status === 'unknown'
              ? handleM365Click
              : () => openSettingsAtTab('workspace')
          }
        />
        {m365PopoverOpen && tenantSharePoint !== null && tenantSharePoint !== undefined && (
          <div
            data-testid={testIds.statusBar.cellM365TenantBadge}
            className="absolute left-0 bg-white z-[9100] text-black"
            style={{
              bottom: STATUS_BAR_HEIGHT,
              minWidth: 320,
              maxWidth: 480,
              fontFamily: 'var(--xp-font-body)',
              fontSize: 12,
              border: '1px solid #7a7a7a',
              boxShadow: '2px 2px 5px rgba(0,0,0,0.25)',
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div style={{ fontWeight: 'bold', color: '#008000' }}>
              Connected to real SharePoint
            </div>
            <div style={{ color: '#374151' }}>
              {tenantSharePoint.displayName}
            </div>
            <div style={{ marginTop: 4 }}>
              <span style={{ color: '#374151' }}>Site:</span>{' '}
              <code
                data-testid={testIds.statusBar.cellM365TenantSiteId}
                style={{
                  fontFamily: 'var(--xp-font-mono)',
                  fontSize: 11,
                  wordBreak: 'break-all',
                  color: '#000',
                }}
              >
                {tenantSharePoint.siteId}
              </code>
            </div>
            <div>
              <span style={{ color: '#374151' }}>Drive:</span>{' '}
              <code
                data-testid={testIds.statusBar.cellM365TenantDriveId}
                style={{
                  fontFamily: 'var(--xp-font-mono)',
                  fontSize: 11,
                  wordBreak: 'break-all',
                  color: '#000',
                }}
              >
                {tenantSharePoint.driveId ?? '(read-only — no drive)'}
              </code>
            </div>
            {tenantSiteWebUrl !== null && (
              <a
                href={tenantSiteWebUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={testIds.statusBar.cellM365TenantOpenLink}
                style={{
                  marginTop: 6,
                  color: 'var(--xp-selection)',
                  textDecoration: 'underline',
                }}
              >
                View in SharePoint ↗
              </a>
            )}
          </div>
        )}
      </div>
      <StatusBarCell
        cell={portalCell}
        testId={testIds.statusBar.cellPortal}
        onClick={() => openWindow('/documents')}
      />
      <StatusBarCell
        cell={indexCell}
        testId={testIds.statusBar.cellIndex}
        onClick={() => openWindow('/corpus-search')}
      />
      <div ref={userBtnRef} className="relative flex">
        <StatusBarCell
          cell={userCell}
          testId={testIds.statusBar.cellUser}
          onClick={
            isSignedIn === true
              ? () => setUserMenuOpen((v) => !v)
              : undefined
          }
        />
        {userMenuOpen && isSignedIn === true && (
          <div
            data-testid={testIds.statusBar.userMenu}
            className="absolute right-0 bg-white z-[9100]"
            style={{
              bottom: STATUS_BAR_HEIGHT,
              minWidth: 160,
              fontFamily: 'var(--xp-font-body)',
              fontSize: 12,
              border: '1px solid #7a7a7a',
              boxShadow: '2px 2px 5px rgba(0,0,0,0.25)',
              padding: '2px 0',
            }}
          >
            <button
              type="button"
              data-testid={testIds.statusBar.signOutMenuItem}
              onClick={() => {
                setUserMenuOpen(false);
                void signOut();
              }}
              className="block w-full text-left px-3 py-1 hover:bg-[var(--xp-selection)] hover:text-white text-black"
              style={{
                fontFamily: 'var(--xp-font-body)',
                fontSize: 12,
                background: 'transparent',
                border: 'none',
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
